import { DI } from 'aurelia';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator, httpsCallable, type Functions } from 'firebase/functions';

export interface IFirebase {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  functions: Functions;
  usingEmulators: boolean;
  call<TReq, TRes>(name: string, data?: TReq): Promise<TRes>;
}

export const IFirebase = DI.createInterface<IFirebase>('IFirebase', (x) => x.singleton(FirebaseService));

export class FirebaseService implements IFirebase {
  readonly app: FirebaseApp;
  readonly auth: Auth;
  readonly db: Firestore;
  readonly functions: Functions;
  readonly usingEmulators: boolean;

  constructor() {
    const env = import.meta.env;
    this.app = initializeApp({
      apiKey: env.VITE_FIREBASE_API_KEY ?? 'AIzaSyAlipXtZjW1JXg4BdfDJjp4PyLfdyEHlpg',
      authDomain: env.VITE_FIREBASE_AUTH_DOMAIN ?? 'bingo-dash-b0078.firebaseapp.com',
      projectId: env.VITE_FIREBASE_PROJECT_ID ?? 'bingo-dash-b0078',
      storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET ?? 'bingo-dash-b0078.appspot.com',
      messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '259655119199',
      appId: env.VITE_FIREBASE_APP_ID ?? '1:259655119199:web:2afb8972e1456ac49106ea',
    });
    this.auth = getAuth(this.app);
    this.db = getFirestore(this.app);
    this.functions = getFunctions(this.app, env.VITE_FUNCTIONS_REGION ?? 'us-central1');
    this.usingEmulators = env.DEV && env.VITE_USE_EMULATORS === 'true';

    if (this.usingEmulators) {
      connectAuthEmulator(this.auth, 'http://127.0.0.1:9099', { disableWarnings: true });
      connectFirestoreEmulator(this.db, '127.0.0.1', 8080);
      connectFunctionsEmulator(this.functions, '127.0.0.1', 5001);
    }
  }

  async call<TReq, TRes>(name: string, data?: TReq): Promise<TRes> {
    const fn = httpsCallable<TReq, TRes>(this.functions, name);
    const res = await fn(data as TReq);
    return res.data;
  }
}
